// paex-apply-discount.js — <paex-apply-discount> — a promotion-banner CTA that
// applies a discount code straight to the cart (no /discount/CODE redirect,
// no full page reload).
//
// Single-hop apply (perf pass, Sasha 2026-08-18). The banner code deliberately
// does NOT stack with a code the shopper already entered (merchant decision
// 2026-08-18 — the banner code wins). That is the whole unlock here: both
// requests below drive the cart's manual-code set to exactly [code], so they
// are idempotent with respect to each other, order does not matter, and there
// is no race. Which lets the cookie prime come OFF the critical path:
//
//   Background, fire-and-forget — GET /discount/CODE (redirect:follow,
//     same-origin credentials, keepalive). Sets the SAME persistent discount
//     cookie a shared /discount/CODE link sets, so Shopify auto-applies the
//     code later the moment an eligible item is in the cart. Nothing waits on
//     it EXCEPT the applicable:false branch, where the cookie is the only
//     thing delivering on the "saved for later" promise and there is no
//     repaint to hurry. keepalive lets it finish across a cart/checkout
//     navigation.
//   Critical path, ONE hop — POST /cart/update.js with { discount: code }.
//     Applies, validates response.discount_codes, and — only when the UI will
//     still be on screen — patches the header bubble + drawer through
//     SectionDynamicUpdate (the ONLY way Ascent re-renders cart UI), then
//     publishes PUB_SUB_EVENTS.cartUpdate. Shared cart primitives come from
//     PaexCartBridge (emitted right before this file, defer order guarantees
//     it runs first); every lookup is at call time and degrades to a no-op
//     exactly like the native typeof-gates.
//
// The previous version chained GET /discount/CODE -> POST /cart/update.js and
// merged the shopper's existing codes off the drawer pills. Measured
// 2026-08-18: that was 3 network hops and 143,690 bytes per click, of which
// the redundant merge POST alone was 136,758 bytes and its own cart-state
// answer was already sitting in the discarded GET response. The merge is gone
// (no stacking wanted) and the GET no longer gates anything.
//
// Why data-after cart/checkout skips `sections` entirely: the drawer section
// measured ~127KB and a full page load follows immediately, so rendering,
// transferring, DOMParser-ing and injecting it is pure waste. Those modes
// navigate as soon as the code validates.
//
// Note: /cart/update.js `discount` REPLACES the cart's whole manual-code set
// (same Shopify behavior paex-cart.js:removeCode relies on). That replacement
// IS the desired no-stacking behavior — a code entered earlier is dropped
// rather than left in discount_codes as applicable:false.
//
// Feedback goes through the built-in freeze-proof toast below (NOT
// webvista.popToast, NOT ModalDialog) — three outcomes:
//   applicable:true  -> data-success (success styling).
//   applicable:false -> data-saved (ALSO success styling — the background
//      cookie prime makes this a positive outcome: empty cart or no eligible
//      line yet, the code auto-applies when one lands).
//   hard failure     -> data-error (error styling) — network error or non-2xx
//      from /cart/update.js ONLY.
// Honest tradeoff: a genuinely invalid/typo code also reports applicable:false
// and therefore ALSO shows the green "saved" message — no public storefront
// API distinguishes invalid from not-currently-eligible without eligible
// lines in the cart. Merchant-configured codes are validated at build /
// placement time instead.
// Companion stylesheet: paex-apply-discount.css (emit it wherever this
// script is emitted). Element attributes: data-code (required), data-after
// (drawer|cart|checkout|none), data-success / data-saved / data-error (toast
// copy, {code} placeholder supported on the error), data-dismiss-label (✕).

(function () {
  'use strict';
  if (customElements.get('paex-apply-discount')) return;

  function bridge() {
    return window.PaexCartBridge || null;
  }

  // Deliberately duplicated from PaexCartBridge.routeRoot rather than
  // delegated: URL building must keep working when the bridge failed to load,
  // same graceful-degradation contract as the typeof-gates below.
  function routeRoot() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || routeRoot() + 'cart/update.js';
  }

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || routeRoot() + 'cart';
  }

  function strings() {
    return window.cartStrings || {};
  }

  // ---- Freeze-proof toast (Sasha 2026-08-14) -------------------------------
  // A plain fixed-position node appended to <body>. Freeze-proof BY
  // CONSTRUCTION: no ModalDialog, no webvista.disablePageScroll, no trapFocus,
  // no overlay — the live region is pointer-events:none and only the toast
  // itself accepts pointer events, so the page stays scrollable and clickable
  // no matter what happens in here. One toast at a time (rapid applies REPLACE,
  // never stack). Auto-removes after 3s; the ✕ removes it immediately. Styles
  // live in paex-apply-discount.css; colors arrive ONLY through the
  // --paex-toast-* custom properties (section color settings), with scheme-var
  // fallbacks per RULES §6b — nothing hardcoded here.
  var TOAST_MS = 3000;
  var TOAST_FADE_MS = 250;
  var TOAST_TOKENS = ['--paex-toast-bg', '--paex-toast-fg', '--paex-toast-border'];
  var toastState = { region: null, el: null, hideTimer: null };

  function toastRegion() {
    if (toastState.region && document.body.contains(toastState.region)) return toastState.region;
    var region = document.createElement('div');
    region.className = 'paex-toast-region';
    region.setAttribute('aria-live', 'polite'); // created once, reused
    document.body.appendChild(region);
    toastState.region = region;
    return region;
  }

  function removeToast(immediate) {
    var el = toastState.el;
    if (toastState.hideTimer) {
      clearTimeout(toastState.hideTimer);
      toastState.hideTimer = null;
    }
    toastState.el = null;
    if (!el) return;
    if (immediate) {
      el.remove();
      return;
    }
    el.classList.remove('is-visible');
    // Plain timeout, not transitionend: also removes when reduced-motion
    // disables the transition (transitionend would never fire).
    setTimeout(function () { el.remove(); }, TOAST_FADE_MS);
  }

  // Custom properties don't cross the append-to-body boundary, so the
  // section-defined --paex-toast-* values have to be read off the triggering
  // element and re-applied to the toast node. Read ONCE per element and cached
  // by the caller (see apply): this getComputedStyle used to run immediately
  // after patchSections had swapped two innerHTML blocks, which forced a full
  // synchronous style recalc at the worst moment on the click path. Missing
  // tokens fall through to the CSS scheme-var fallbacks.
  function readToastTokens(source) {
    var tokens = {};
    if (!source) return tokens;
    try {
      var computed = getComputedStyle(source);
      TOAST_TOKENS.forEach(function (name) {
        var value = computed.getPropertyValue(name);
        if (value && value.trim()) tokens[name] = value.trim();
      });
    } catch (error) {
      /* colors fall back to scheme vars */
    }
    return tokens;
  }

  function toast(message, type, source, tokens) {
    if (!message) return;
    var region = toastRegion();
    removeToast(true); // replace, never stack

    var el = document.createElement('div');
    el.className = 'paex-apply-toast';
    if (type === 'error' || type === 'warning') el.classList.add('paex-apply-toast--error');

    var text = document.createElement('span');
    text.className = 'paex-apply-toast__text';
    text.textContent = message; // DOM APIs only — no innerHTML

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'paex-apply-toast__close';
    close.setAttribute('aria-label', (source && source.getAttribute('data-dismiss-label')) || 'Close');
    close.textContent = '✕';
    close.addEventListener('click', function () { removeToast(true); });

    el.appendChild(text);
    el.appendChild(close);

    var resolved = tokens || readToastTokens(source);
    Object.keys(resolved).forEach(function (name) {
      el.style.setProperty(name, resolved[name]);
    });

    region.appendChild(el);

    // Double rAF so the entry transition actually runs (single frame can be
    // coalesced); with reduced motion the CSS drops the transition entirely.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { el.classList.add('is-visible'); });
    });

    toastState.el = el;
    toastState.hideTimer = setTimeout(function () { removeToast(false); }, TOAST_MS);
  }

  // silence-gated drawer stays out of the section set, same as paex-attach.js
  function activeDrawer() {
    var drawer = document.getElementById('Cart-Drawer');
    if (drawer && drawer.hasAttribute('data-status-silence')) return null;
    return drawer;
  }

  function sectionsToRender() {
    var b = bridge();
    if (b) return b.sectionsToRender(activeDrawer());
    return [{ id: 'Cart-Icon-Bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }];
  }

  // Takes the descriptor list resolved once per click, so the set that was
  // REQUESTED is always the set that gets APPLIED (calling sectionsToRender a
  // second time here could disagree with the first if the drawer's
  // data-status-silence changed mid-flight).
  function patchSections(response, sections) {
    if (!sections || !response || !response.sections) return;
    if (typeof SectionDynamicUpdate === 'undefined') return;
    SectionDynamicUpdate.updateSections(sections, response.sections);
    var b = bridge();
    if (b) b.retrapDrawer(null);
  }

  function publishCart(response) {
    var b = bridge();
    if (b) b.publishCart('paex-apply-discount', response);
  }

  function isApplicable(response, code) {
    if (!response || !Array.isArray(response.discount_codes)) return null; // unknown
    var match = response.discount_codes.filter(function (entry) {
      return entry && (entry.code || '').toLowerCase() === code.toLowerCase();
    });
    if (!match.length) return null;
    return match[0].applicable === true;
  }

  customElements.define(
    'paex-apply-discount',
    class PaexApplyDiscount extends HTMLElement {
      connectedCallback() {
        this.busy = false;
        this.toastTokens = null;
        this.onClick = this.apply.bind(this);
        this.onKey = function (event) {
          var key = event.code ? event.code.toUpperCase() : '';
          if (key === 'ENTER' || key === 'SPACE') {
            event.preventDefault();
            this.apply();
          }
        }.bind(this);
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKey);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKey);
      }

      apply() {
        if (this.busy) return;
        var code = (this.getAttribute('data-code') || '').trim();
        if (!code) return;

        // In the theme editor section re-renders can fight the live preview —
        // don't mutate the merchant's own cart from design mode.
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          toast((window.copyStrings && window.copyStrings.copyDisabled) || '', 'warning', this);
          return;
        }

        this.busy = true;
        this.classList.add('loading');
        this.setAttribute('aria-busy', 'true');

        // Everything DOM-dependent is resolved ONCE here — before any network
        // work, and before a section repaint can invalidate style.
        var after = this.getAttribute('data-after') || 'drawer';
        var navigates = after === 'cart' || after === 'checkout';
        var sections = navigates ? null : sectionsToRender();
        var tokens = this.toastTokens || (this.toastTokens = readToastTokens(this));

        // Cookie prime — FIRE AND FORGET. Both this and the POST below set the
        // cart's manual-code set to exactly [code], so it is idempotent and
        // order-independent; nothing has to wait for it. keepalive lets it
        // finish across the cart/checkout navigation.
        var prime = fetch(routeRoot() + 'discount/' + encodeURIComponent(code), {
          redirect: 'follow',
          credentials: 'same-origin',
          keepalive: true,
        }).catch(function () { return null; });

        // No merge: the banner code REPLACES whatever the shopper had.
        var payload = { discount: code };
        if (sections) {
          payload.sections = sections.map(function (s) { return s.section; }).join(',');
          payload.sections_url = window.location.pathname;
        }

        fetch(cartUpdateUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        })
          .then(function (response) { return response.ok ? response.json() : null; })
          .then(function (response) {
            if (!response) throw new Error('cart-update-failed');

            if (isApplicable(response, code) === false) {
              // Not applicable RIGHT NOW — empty cart, or no eligible line for
              // an item-restricted code. Not an error: the cookie prime makes
              // the code auto-apply once an eligible item lands. This is the
              // ONE branch that waits for the prime, because the cookie is the
              // whole promise being made and there is no repaint to hurry.
              return prime.then(function () {
                toast(
                  this.getAttribute('data-saved') ||
                    'Code saved — it will be applied automatically when you add an eligible item.',
                  'success',
                  this,
                  tokens
                );
              }.bind(this));
            }

            // Navigating? Leave NOW. patchSections + publishCart + the toast
            // would all be discarded by the page load milliseconds later.
            if (navigates) {
              window.location.href = after === 'checkout' ? routeRoot() + 'checkout' : cartUrl();
              return;
            }

            // Success — repaint the cart UI, announce, then run the follow-up.
            patchSections(response, sections);
            publishCart(response);
            toast(this.getAttribute('data-success') || strings().applyCodeSuccess || 'Discount applied!', 'success', this, tokens);
            if (after === 'drawer') this.openDrawer();
            // after === 'none' -> the toast is the only feedback
          }.bind(this))
          .catch(function () {
            // Hard failures ONLY (network error / non-2xx from update.js).
            var failed = (this.getAttribute('data-error') || strings().error || 'Could not apply the code. Please try again.')
              .replace('[code]', code)
              .replace('{code}', code);
            toast(failed, 'error', this, tokens);
          }.bind(this))
          .finally(function () {
            this.busy = false;
            this.classList.remove('loading');
            this.removeAttribute('aria-busy');
          }.bind(this));
      }

      openDrawer() {
        var drawer = activeDrawer();
        if (drawer && typeof drawer.show === 'function') {
          drawer.show(this);
        }
      }
    }
  );
})();
